/**
 * Dynamic KPI Chart Component
 *
 * Wrapper that selects appropriate chart type (Line/Area/Bar) based on KPI metadata
 * Includes time range selector and threshold indicators
 */

import { LineChart } from './LineChart';
import { AreaChart } from './AreaChart';
import { BarChart } from './BarChart';
import type { TimeRange } from '../../hooks/useDynamicChart';
import type { KPIMetadata } from '../../utils/kpiMetadata';

interface DynamicKPIChartProps {
  data: any[];
  metadata: KPIMetadata;
  timeRange: TimeRange;
  setTimeRange: (range: TimeRange) => void;
  loading: boolean;
}

export function DynamicKPIChart({
  data,
  metadata,
  timeRange,
  setTimeRange,
  loading,
}: DynamicKPIChartProps) {
  // Time range buttons
  const timeRanges: TimeRange[] = ['1h', '24h', '7d', '30d', '90d'];

  // Chart configuration
  const series = [
    {
      name: metadata.displayName,
      dataKey: 'value',
      color: metadata.color,
      unit: metadata.unit,
      strokeWidth: 3,
      dot: false,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Time Range Selector */}
      <div className="flex gap-2">
        {timeRanges.map((range) => (
          <button
            key={range}
            onClick={() => setTimeRange(range)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              timeRange === range
                ? 'bg-djin-cyan text-djin-navy'
                : 'bg-slate-700/30 text-slate-300 hover:bg-slate-700/50'
            }`}
            style={
              timeRange === range
                ? {
                    backgroundColor: '#00D4FF',
                    color: '#0A0E1A',
                  }
                : undefined
            }
          >
            {range.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Chart - Select based on metadata.chartType */}
      {metadata.chartType === 'line' && (
        <LineChart
          data={data}
          series={series}
          height={500}
          showBrush={false}
          showLegend={false}
          yAxisDomain={(metadata.yAxisDomain as [number, number] | ['auto', 'auto']) || ['auto', 'auto']}
          loading={loading}
        />
      )}

      {metadata.chartType === 'area' && (
        <AreaChart
          data={data}
          series={series}
          height={500}
          showBrush={false}
          showLegend={false}
          yAxisDomain={(metadata.yAxisDomain as [number, number] | ['auto', 'auto']) || ['auto', 'auto']}
          loading={loading}
        />
      )}

      {metadata.chartType === 'bar' && (
        <BarChart
          data={data}
          series={series}
          height={500}
          showLegend={false}
          yAxisDomain={(metadata.yAxisDomain as [number, number] | ['auto', 'auto']) || ['auto', 'auto']}
          loading={loading}
        />
      )}

      {/* Threshold Indicators (if defined) */}
      {metadata.thresholds && (
        <div className="flex gap-4 text-sm">
          {metadata.thresholds.warning && (
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: metadata.thresholds.warning.color }}
              />
              <span className="text-slate-300">
                {metadata.thresholds.warning.label} (&lt;{' '}
                {metadata.thresholds.warning.value}
                {metadata.unit})
              </span>
            </div>
          )}
          {metadata.thresholds.critical && (
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: metadata.thresholds.critical.color }}
              />
              <span className="text-slate-300">
                {metadata.thresholds.critical.label} (&lt;{' '}
                {metadata.thresholds.critical.value}
                {metadata.unit})
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
